import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { GraduationCap, Users, Camera, Sparkles, CheckCircle2, Calendar } from "lucide-react";

const tracks = [
  {
    icon: Camera,
    title: "FUNDAMENTALS WORKSHOP",
    desc: "Hands-on group class covering exposure, lighting, composition, and shooting tethered in a professional studio.",
    duration: "3 hours · small group",
  },
  {
    icon: Sparkles,
    title: "LIGHTING INTENSIVE",
    desc: "Studio strobes, modifiers, and continuous lighting walkthrough using our cyclorama wall and beauty station.",
    duration: "4 hours · hands-on",
  },
  {
    icon: Users,
    title: "1:1 MENTORING",
    desc: "Private sessions tailored to your portfolio: portraits, fashion, product, or building a freelance business in Columbus.",
    duration: "90 min · private",
  },
];

const outcomes = [
  "Confidently shoot tethered in a pro studio environment",
  "Build a 3-light portrait setup from scratch",
  "Direct and pose subjects for portraits and fashion",
  "Use the cyclorama wall for full-length and product work",
  "Plan and price client sessions in the Columbus, GA market",
  "Leave with portfolio-ready frames from each session",
];

const faqEntries = [
  {
    q: "Do you offer photography classes in Columbus, GA?",
    a: "Yes. MyStudio runs photography workshops and 1:1 mentoring in Columbus, GA covering studio lighting, posing, tethered shooting, and building a freelance photo business.",
  },
  {
    q: "Who are the workshops for?",
    a: "Beginners learning their first camera, hobbyists leveling up to studio work, and working photographers who want hands-on time with pro gear and feedback from a mentor.",
  },
  {
    q: "What is included in a mentoring session?",
    a: "Studio time, lighting and backdrops, tethered capture, and a working portfolio review. You leave with edited frames, a shot list template, and a personalized growth plan.",
  },
  {
    q: "How much do photography workshops cost?",
    a: "Group workshops and 1:1 mentoring are priced per session. Reach out via the contact page for the current schedule, seats remaining, and private mentoring availability.",
  },
  {
    q: "Can I bring my own camera?",
    a: "Yes — bring your camera, lens, and a memory card. We provide the lighting, modifiers, backdrops, cyclorama wall, and tethering setup.",
  },
];

const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  name: "Photography Workshops & Mentoring — Columbus, GA",
  description:
    "Hands-on photography classes and 1:1 mentoring in Columbus, GA. Studio lighting, posing, tethered shooting, and freelance business coaching at MyStudio.",
  provider: {
    "@type": "Organization",
    name: "MyStudio",
    sameAs: "https://bookmystudiospace.com",
  },
  educationalCredentialAwarded: "Certificate of completion",
  hasCourseInstance: [
    {
      "@type": "CourseInstance",
      courseMode: "Onsite",
      location: {
        "@type": "Place",
        name: "MyStudio",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Columbus",
          addressRegion: "GA",
          addressCountry: "US",
        },
      },
    },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqEntries.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const Workshops = () => (
  <div className="pt-20">
    <Helmet>
      <title>Photography Classes Columbus GA | Workshops & Mentoring | MyStudio</title>
      <meta
        name="description"
        content="Photography classes and 1:1 mentoring in Columbus, GA. Hands-on studio lighting, posing, tethered shooting, and freelance coaching at MyStudio."
      />
      <link rel="canonical" href="https://bookmystudiospace.com/workshops" />
      <meta property="og:title" content="Photography Classes Columbus GA | Workshops & Mentoring" />
      <meta
        property="og:description"
        content="Hands-on photography workshops and private mentoring in Columbus, GA. Studio lighting, posing, and freelance business coaching."
      />
      <meta property="og:url" content="https://bookmystudiospace.com/workshops" />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </Helmet>

    <section className="py-20 text-center border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
          <GraduationCap className="w-4 h-4" /> Workshops & Mentoring
        </div>
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-6">
          PHOTOGRAPHY CLASSES IN COLUMBUS, GA
        </h1>
        <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto mb-8">
          MyStudio is a learning hub for Columbus photographers. Group workshops and 1:1 mentoring
          inside a working studio — pro lighting, cyclorama wall, tethered capture, and real
          portfolio-building feedback.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-8">
              Request Workshop Dates
            </Button>
          </Link>
          <Link to="/book">
            <Button variant="outline" className="border-border font-body px-8">
              Book a Mentor Session
            </Button>
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          CHOOSE YOUR TRACK
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {tracks.map(({ icon: Icon, title, desc, duration }) => (
            <div key={title} className="bg-panel border border-border p-6">
              <Icon className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-display tracking-widest text-base mb-2">{title}</h3>
              <p className="text-sm font-body text-foreground/80 mb-4">{desc}</p>
              <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase">
                {duration}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          WHAT YOU&apos;LL LEAVE WITH
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {outcomes.map((o) => (
            <div key={o} className="bg-panel border border-border p-5 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-body text-foreground/85">{o}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          HOW MENTORING WORKS
        </h2>
        <ol className="space-y-6">
          {[
            ["INTRO CALL", "We talk through your goals, current portfolio, and where you want to be in 6 months."],
            ["STUDIO SESSION", "A focused 90-minute working session inside MyStudio with a live model or your own subject."],
            ["PORTFOLIO REVIEW", "Edited frames, a critique, and a written growth plan you can act on right away."],
          ].map(([title, body], i) => (
            <li key={title} className="flex gap-4 bg-panel border border-border p-6">
              <span className="font-display text-primary text-2xl tracking-widest shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display tracking-widest text-base mb-2">{title}</h3>
                <p className="text-sm font-body text-foreground/80">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          WORKSHOP FAQS
        </h2>
        <div className="space-y-4">
          {faqEntries.map((f) => (
            <div key={f.q} className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-primary text-base mb-2">{f.q}</h3>
              <p className="text-sm font-body text-foreground/80">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 space-y-4">
          <Link to="/contact">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10">
              <Calendar className="w-4 h-4 mr-2" /> Request Workshop Dates
            </Button>
          </Link>
          <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase">
            Also see <Link to="/faq" className="text-primary underline">full studio FAQ</Link> ·{" "}
            <Link to="/columbus-ga-photo-studio-rental" className="text-primary underline">
              studio rental
            </Link>
          </p>
        </div>
      </div>
    </section>
  </div>
);

export default Workshops;